/**
 * Financial Hooks
 * 
 * Custom React hooks for the financial module using React Query
 * for data fetching, caching, and state management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  budgetPeriodService,
  agentService,
  financialListService,
  investmentInstitutionService,
  revenueService,
  futureExpenseService,
  monthlyExpenseService,
  dailyExpenseService,
  dashboardService,
  financialSettingsService,
} from '@/services/financial.service';
import type {
  BudgetPeriod,
  BudgetPeriodCreate,
  BudgetPeriodUpdate,
  AgentCreate,
  AgentUpdate,
  FinancialListType,
  FinancialListItemCreate,
  RevenueCreate,
  RevenueUpdate,
  RevenueRealization,
  FractionalRealizationCreate,
  FutureExpenseCreate,
  FutureExpenseCalculationRequest,
  FutureExpenseUpdate,
  FutureExpenseRealizationCreate,
  FutureExpenseRealizationUpdate,
  FutureExpenseExecuteRequest,
  InvestmentInstitutionCreate,
  InvestmentInstitutionUpdate,
  MonthlyExpenseCreate,
  MonthlyExpenseUpdate,
  MonthlyExpenseRealization,
  DailyExpenseCreate,
  DailyExpenseUpdate,
  DailyExpenseRealizationCreate,
  DailyExpenseRealizationUpdate,
  UserFinancialSettingsUpdate,
} from '@/types/financial';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth.context';
import {
  clearSelectedBudgetPeriodId,
  getSelectedBudgetPeriodId,
  setSelectedBudgetPeriodId,
} from '@/lib/budget-period-session';
import { UPCOMING_DAYS } from '@/lib/dueWindow';

// Query Keys
/** Escopo de dados na cópia de receitas entre períodos. */
export type RevenueCopyData = 'planned' | 'realized' | 'both';

export const financialKeys = {
  all: ['financial'] as const,
  budgetPeriods: () => [...financialKeys.all, 'budgetPeriods'] as const,
  budgetPeriod: (id: string) => [...financialKeys.budgetPeriods(), id] as const,
  budgetPeriodSummary: (id: string) => [...financialKeys.budgetPeriod(id), 'summary'] as const,
  currentPeriod: () => [...financialKeys.budgetPeriods(), 'current'] as const,
  pendingCarryover: (id: string) =>
    [...financialKeys.budgetPeriod(id), 'carryover', 'pending'] as const,
  navigation: () => [...financialKeys.budgetPeriods(), 'navigation'] as const,
  
  agents: () => [...financialKeys.all, 'agents'] as const,
  agent: (id: string) => [...financialKeys.agents(), id] as const,
  
  lists: () => [...financialKeys.all, 'lists'] as const,
  list: (type: FinancialListType) => [...financialKeys.lists(), type] as const,

  investmentInstitutions: () => [...financialKeys.all, 'investmentInstitutions'] as const,
  
  revenues: (periodId?: string) => [...financialKeys.all, 'revenues', periodId] as const,
  revenue: (id: string) => [...financialKeys.revenues(), id] as const,
  overdueRevenues: (periodId?: string) => [...financialKeys.all, 'revenues', 'overdue', periodId] as const,
  upcomingRevenues: (periodId?: string) => [...financialKeys.all, 'revenues', 'upcoming', periodId] as const,
  revenueCopySourcePeriods: (targetPeriodId?: string, copyData: RevenueCopyData = 'planned') =>
    [...financialKeys.all, 'revenues', 'copySourcePeriods', targetPeriodId, copyData] as const,
  
  futureExpenses: () => [...financialKeys.all, 'futureExpenses'] as const,
  futureExpense: (id: string) => [...financialKeys.futureExpenses(), id] as const,
  futureExpenseDraftCalculation: (
    params: FutureExpenseCalculationRequest,
    periodId?: string
  ) => [...financialKeys.futureExpenses(), 'draftCalculation', periodId, params] as const,
  
  monthlyExpenses: (periodId?: string) => [...financialKeys.all, 'monthlyExpenses', periodId] as const,
  monthlyExpense: (id: string) => [...financialKeys.monthlyExpenses(), id] as const,
  overdueExpenses: (periodId?: string) => [...financialKeys.monthlyExpenses(), 'overdue', periodId] as const,
  upcomingExpenses: (periodId?: string) => [...financialKeys.monthlyExpenses(), 'upcoming', periodId] as const,
  
  dailyExpenses: (periodId?: string) => [...financialKeys.all, 'dailyExpenses', periodId] as const,
  dailyExpense: (id: string) => [...financialKeys.dailyExpenses(), id] as const,
  dailyExpenseCopySourcePeriods: (
    targetPeriodId?: string,
    copyData: 'planned' | 'realized' = 'planned'
  ) => [...financialKeys.all, 'dailyExpenses', 'copySourcePeriods', targetPeriodId, copyData] as const,
  
  dashboard: (periodId?: string) => [...financialKeys.all, 'dashboard', periodId] as const,
  kpis: (periodId?: string) => [...financialKeys.dashboard(periodId), 'kpis'] as const,
  alerts: () => [...financialKeys.all, 'alerts'] as const,
  
  settings: () => [...financialKeys.all, 'settings'] as const,
};

const resolveSessionBudgetPeriodId = async (userId?: string | null): Promise<string | null> => {
  const storedPeriodId = getSelectedBudgetPeriodId(userId);
  if (storedPeriodId) {
    return storedPeriodId;
  }

  const currentPeriod = await budgetPeriodService.getCurrent();
  setSelectedBudgetPeriodId(userId, currentPeriod.id);
  return currentPeriod.id;
};

const getApiErrorMessage = (error: unknown): string => {
  if (typeof error !== 'object' || error === null) {
    return 'Ocorreu um erro inesperado.';
  }

  const typedError = error as {
    message?: unknown;
    response?: {
      data?: {
        detail?: unknown;
        message?: unknown;
      };
    };
  };

  const detail = typedError.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail;
  }

  const message = typedError.response?.data?.message;
  if (typeof message === 'string' && message.trim().length > 0) {
    return message;
  }

  if (typeof typedError.message === 'string' && typedError.message.trim().length > 0) {
    return typedError.message;
  }

  return 'Ocorreu um erro inesperado.';
};

// ============================================
// Budget Period Hooks
// ============================================

export function useCurrentBudgetPeriod() {
  const { user } = useAuth();

  return useQuery({
    queryKey: financialKeys.currentPeriod(),
    enabled: !!user?.id,
    queryFn: async () => {
      const selectedPeriodId = getSelectedBudgetPeriodId(user?.id);

      if (selectedPeriodId) {
        try {
          return await budgetPeriodService.getById(selectedPeriodId);
        } catch {
          clearSelectedBudgetPeriodId(user?.id);
        }
      }

      const currentPeriod = await budgetPeriodService.getCurrent();
      setSelectedBudgetPeriodId(user?.id, currentPeriod.id);
      return currentPeriod;
    },
  });
}

/**
 * Saldo do PO anterior aguardando decisão do usuário.
 *
 * Devolve `null` quando não há pergunta a fazer. Fica desabilitado sem período,
 * e não tenta de novo em erro: se a consulta falhar, o certo é não exibir o
 * modal — nunca inventar uma proposta de valor.
 */
export function useActiveBudgetPeriod() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...financialKeys.budgetPeriods(), 'active'],
    enabled: !!user?.id,
    queryFn: () => budgetPeriodService.getCurrent(),
  });
}

export function usePendingCarryover(periodId?: string) {
  return useQuery({
    queryKey: financialKeys.pendingCarryover(periodId ?? ''),
    enabled: !!periodId,
    retry: false,
    queryFn: () => budgetPeriodService.getPendingCarryover(periodId as string),
  });
}

/**
 * Invalida tudo que depende do saldo do período. O aceite cria um lançamento
 * novo, então cards, listas e relatórios precisam ser relidos.
 */
function useCarryoverDecisionMutation(
  acao: (periodId: string) => Promise<BudgetPeriod>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (periodId: string) => acao(periodId),
    onSuccess: async (period) => {
      queryClient.setQueryData(financialKeys.currentPeriod(), period);
      queryClient.setQueryData(financialKeys.budgetPeriod(period.id), period);
      queryClient.setQueryData(financialKeys.pendingCarryover(period.id), null);

      await queryClient.invalidateQueries({ queryKey: financialKeys.all });
    },
    onError: (error) => {
      // Sem isto o usuário fica clicando num botão que não responde: o modal é
      // não-dispensável de propósito, então uma falha silenciosa o prende ali.
      toast({
        title: 'Não foi possível registrar sua decisão',
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
}

export function useAcceptCarryover() {
  return useCarryoverDecisionMutation(budgetPeriodService.acceptCarryover);
}

export function useDeclineCarryover() {
  return useCarryoverDecisionMutation(budgetPeriodService.declineCarryover);
}

export function useBudgetPeriodNavigation() {
  const { user } = useAuth();

  return useQuery({
    queryKey: financialKeys.navigation(),
    enabled: !!user?.id,
    queryFn: async () => {
      const selectedPeriodId = await resolveSessionBudgetPeriodId(user?.id);
      const navigation = await budgetPeriodService.getNavigation(selectedPeriodId ?? undefined);

      if (navigation.current_period?.id) {
        setSelectedBudgetPeriodId(user?.id, navigation.current_period.id);
        return navigation;
      }

      clearSelectedBudgetPeriodId(user?.id);

      const currentPeriod = await budgetPeriodService.getCurrent();
      setSelectedBudgetPeriodId(user?.id, currentPeriod.id);
      return budgetPeriodService.getNavigation(currentPeriod.id);
    },
  });
}

export function useBudgetPeriods(page = 1, pageSize = 12) {
  return useQuery({
    queryKey: [...financialKeys.budgetPeriods(), page, pageSize],
    queryFn: () => budgetPeriodService.getAll(page, pageSize),
  });
}

export function useBudgetPeriod(id: string) {
  return useQuery({
    queryKey: financialKeys.budgetPeriod(id),
    queryFn: () => budgetPeriodService.getById(id),
    enabled: !!id,
  });
}

export function useBudgetPeriodSummary(id: string) {
  return useQuery({
    queryKey: financialKeys.budgetPeriodSummary(id),
    queryFn: () => budgetPeriodService.getSummary(id),
    enabled: !!id,
  });
}

export function useCreateBudgetPeriod() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: (data: BudgetPeriodCreate) => budgetPeriodService.create(data),
    onSuccess: async (createdPeriod) => {
      setSelectedBudgetPeriodId(user?.id, createdPeriod.id);
      queryClient.setQueryData(financialKeys.currentPeriod(), createdPeriod);
      await queryClient.invalidateQueries({ queryKey: financialKeys.all });
      await queryClient.refetchQueries({ queryKey: financialKeys.currentPeriod() });
      await queryClient.refetchQueries({ queryKey: financialKeys.navigation() });
      await queryClient.refetchQueries({ queryKey: financialKeys.settings() });
      toast({ title: 'Período orçamentário criado com sucesso!' });
    },
    onError: () => {
      toast({ title: 'Erro ao criar período orçamentário', variant: 'destructive' });
    },
  });
}

export function useUpdateBudgetPeriod() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BudgetPeriodUpdate }) =>
      budgetPeriodService.update(id, data),
    onSuccess: async (updatedPeriod, { id }) => {
      setSelectedBudgetPeriodId(user?.id, updatedPeriod.id);
      queryClient.setQueryData(financialKeys.budgetPeriod(id), updatedPeriod);
      queryClient.setQueryData(financialKeys.currentPeriod(), updatedPeriod);
      queryClient.setQueryData(financialKeys.budgetPeriod(updatedPeriod.id), updatedPeriod);

      await queryClient.invalidateQueries({ queryKey: financialKeys.all });
      await queryClient.refetchQueries({ queryKey: financialKeys.currentPeriod() });
      await queryClient.refetchQueries({ queryKey: financialKeys.navigation() });
      await queryClient.refetchQueries({ queryKey: financialKeys.settings() });
      toast({ title: 'Período atualizado com sucesso!' });
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar período', variant: 'destructive' });
    },
  });
}

export function useNavigatePeriod() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (direction: 'previous' | 'next' | 'current') => {
      const selectedPeriodId = getSelectedBudgetPeriodId(user?.id);
      return budgetPeriodService.navigate(direction, selectedPeriodId ?? undefined);
    },
    onSuccess: async (targetPeriod) => {
      setSelectedBudgetPeriodId(user?.id, targetPeriod.id);
      queryClient.setQueryData(financialKeys.currentPeriod(), targetPeriod);
      queryClient.setQueryData(financialKeys.budgetPeriod(targetPeriod.id), targetPeriod);

      // Force refetch all period-related queries immediately
      await queryClient.refetchQueries({ queryKey: financialKeys.currentPeriod() });
      await queryClient.refetchQueries({ queryKey: financialKeys.navigation() });
      // Invalidate data that depends on the new period
      queryClient.invalidateQueries({ queryKey: financialKeys.budgetPeriods() });
      queryClient.invalidateQueries({ queryKey: financialKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: [...financialKeys.all, 'revenues'] });
      queryClient.invalidateQueries({ queryKey: [...financialKeys.all, 'monthlyExpenses'] });
      queryClient.invalidateQueries({ queryKey: financialKeys.futureExpenses() });
      queryClient.invalidateQueries({ queryKey: [...financialKeys.all, 'dailyExpenses'] });
    },
  });
}

export function useCopyFromPreviousPeriod() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (options: {
      copy_revenues?: boolean;
      copy_future_expenses?: boolean;
      copy_monthly_expenses?: boolean;
      copy_daily_expenses?: boolean;
      revenue_types?: string[];
      expense_types?: string[];
    }) => budgetPeriodService.copyFromPrevious(options),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({
        title: 'Dados copiados com sucesso!',
        description: data.message,
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao copiar dados',
        description: 'Não foi possível copiar os dados do período anterior.',
        variant: 'destructive',
      });
    },
  });
}

// ============================================
// Agent Hooks
// ============================================

export function useAgents(includeInactive = false) {
  return useQuery({
    queryKey: [...financialKeys.agents(), includeInactive],
    queryFn: () => agentService.getAll(includeInactive),
  });
}

export function useAgent(id: string) {
  return useQuery({
    queryKey: financialKeys.agent(id),
    queryFn: () => agentService.getById(id),
    enabled: !!id,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: AgentCreate) => agentService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.agents() });
      toast({ title: 'Agente criado com sucesso!' });
    },
    onError: () => {
      toast({ title: 'Erro ao criar agente', variant: 'destructive' });
    },
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AgentUpdate }) =>
      agentService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: financialKeys.agent(id) });
      queryClient.invalidateQueries({ queryKey: financialKeys.agents() });
      toast({ title: 'Agente atualizado com sucesso!' });
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar agente', variant: 'destructive' });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => agentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.agents() });
      toast({ title: 'Agente removido com sucesso!' });
    },
    onError: () => {
      toast({ title: 'Erro ao remover agente', variant: 'destructive' });
    },
  });
}

// ============================================
// Financial List Hooks
// ============================================

export function useFinancialLists() {
  return useQuery({
    queryKey: financialKeys.lists(),
    queryFn: financialListService.getAll,
    staleTime: 30 * 60 * 1000, // 30 minutes - lists don't change often
  });
}

export function useFinancialList(listType: FinancialListType) {
  return useQuery({
    queryKey: financialKeys.list(listType),
    queryFn: () => financialListService.getByType(listType),
    staleTime: 30 * 60 * 1000,
  });
}

export function useCreateFinancialListItem() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listType, data }: { listType: FinancialListType; data: FinancialListItemCreate }) =>
      financialListService.createItem(listType, data),
    onSuccess: (_, { listType }) => {
      queryClient.invalidateQueries({ queryKey: financialKeys.list(listType) });
      queryClient.invalidateQueries({ queryKey: financialKeys.lists() });
      toast({ title: 'Item adicionado à lista!' });
    },
    onError: () => {
      toast({ title: 'Erro ao adicionar item', variant: 'destructive' });
    },
  });
}

// ============================================
// Investment Institutions Hooks (Local do investimento)
// ============================================

export function useInvestmentInstitutions(includeInactive = false) {
  return useQuery({
    queryKey: [...financialKeys.investmentInstitutions(), includeInactive],
    queryFn: () => investmentInstitutionService.getAll({ include_inactive: includeInactive }),
    staleTime: 30 * 60 * 1000,
  });
}

export function useCreateInvestmentInstitution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InvestmentInstitutionCreate) => investmentInstitutionService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.investmentInstitutions() });
      toast({ title: 'Instituição adicionada com sucesso!' });
    },
    onError: () => {
      toast({ title: 'Erro ao adicionar instituição', variant: 'destructive' });
    },
  });
}

export function useUpdateInvestmentInstitution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: InvestmentInstitutionUpdate }) =>
      investmentInstitutionService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.investmentInstitutions() });
      toast({ title: 'Instituição atualizada com sucesso!' });
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar instituição', variant: 'destructive' });
    },
  });
}

// ============================================
// Revenue Hooks
// ============================================

export function useRevenues(periodId?: string) {
  return useQuery({
    queryKey: financialKeys.revenues(periodId),
    queryFn: () => revenueService.getAll(periodId),
  });
}

export function useRevenuesByPeriod(periodId?: string, enabled = true) {
  return useQuery({
    queryKey: financialKeys.revenues(periodId),
    queryFn: () => revenueService.getAll(periodId),
    enabled: enabled && !!periodId,
  });
}

export function useRevenue(id: string) {
  return useQuery({
    queryKey: financialKeys.revenue(id),
    queryFn: () => revenueService.getById(id),
    enabled: !!id,
  });
}

export function useCreateRevenue() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ data, periodId }: { data: RevenueCreate; periodId?: string }) =>
      revenueService.create(data, periodId),
    onSuccess: () => {
      // Invalidate all financial queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Receita criada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao criar receita', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useUpdateRevenue() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RevenueUpdate }) =>
      revenueService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Receita atualizada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao atualizar receita', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useDeleteRevenue() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, apply_to_future }: { id: string; apply_to_future: boolean }) =>
      revenueService.delete(id, { apply_to_future }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Receita removida com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao remover receita', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useRealizeRevenue() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RevenueRealization }) =>
      revenueService.realize(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Receita realizada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao realizar receita', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useUndoRevenueRealization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => revenueService.undoRealization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Realizado excluído! A receita voltou para planejado.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir realizado',
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
}

export function useAddFractionalRealization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FractionalRealizationCreate }) =>
      revenueService.addFractionalRealization(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Realização parcial adicionada!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao adicionar realização', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useUpdateFractionalRealization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      revenueId,
      realizationId,
      data,
    }: {
      revenueId: string;
      realizationId: string;
      data: FractionalRealizationCreate;
    }) => revenueService.updateFractionalRealization(revenueId, realizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Recebimento atualizado com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar recebimento',
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteFractionalRealization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ revenueId, realizationId }: { revenueId: string; realizationId: string }) =>
      revenueService.deleteFractionalRealization(revenueId, realizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Recebimento removido com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao remover recebimento',
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
}

export function useCopyRevenuesFromPrevious() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (params: {
      target_period_id?: string;
      source_period_id?: string;
      revenue_types?: string[];
      copy_data?: RevenueCopyData;
      revenue_ids?: string[];
    }) => revenueService.copyFromPrevious(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: `${data.copied_count} receitas copiadas com sucesso!` });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao copiar receitas', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useRevenueCopySourcePeriods(
  targetPeriodId: string | undefined,
  copyData: RevenueCopyData = 'planned',
  enabled = true
) {
  return useQuery({
    queryKey: financialKeys.revenueCopySourcePeriods(targetPeriodId, copyData),
    queryFn: () =>
      revenueService.getCopySourcePeriods({
        target_period_id: targetPeriodId!,
        copy_data: copyData,
      }),
    enabled: enabled && !!targetPeriodId,
  });
}

// ============================================
// Future Expense Hooks
// ============================================

export function useFutureExpenses(status?: string, periodId?: string) {
  return useQuery({
    queryKey: [...financialKeys.futureExpenses(), status, periodId],
    queryFn: () => futureExpenseService.getAll(status, periodId),
  });
}

export function useActiveFutureExpenses() {
  return useQuery({
    queryKey: [...financialKeys.futureExpenses(), 'active'],
    queryFn: futureExpenseService.getAllActive,
  });
}

export function useFutureExpense(id: string) {
  return useQuery({
    queryKey: financialKeys.futureExpense(id),
    queryFn: () => futureExpenseService.getById(id),
    enabled: !!id,
  });
}

export function useFutureExpenseCalculation(id: string) {
  return useQuery({
    queryKey: [...financialKeys.futureExpense(id), 'calculation'],
    queryFn: () => futureExpenseService.getCalculation(id),
    enabled: !!id,
  });
}

export function useFutureExpenseDraftCalculation(
  params: FutureExpenseCalculationRequest | null,
  periodId?: string
) {
  const enabled = (() => {
    if (!params) return false;
    if (params.annual_rate === undefined || params.annual_rate === null) return false;

    const missingCount = [
      params.target_amount,
      params.target_date,
      params.present_value,
      params.monthly_payment,
    ].filter((v) => v === undefined || v === null || v === '').length;
    return missingCount === 1;
  })();

  return useQuery({
    queryKey: enabled
      ? financialKeys.futureExpenseDraftCalculation(
          params as FutureExpenseCalculationRequest,
          periodId
        )
      : [...financialKeys.futureExpenses(), 'draftCalculation', periodId, 'disabled'],
    queryFn: () => futureExpenseService.calculate(params as FutureExpenseCalculationRequest, periodId),
    enabled,
  });
}

export function useFutureExpenseRealizationSheet(id: string) {
  return useQuery({
    queryKey: [...financialKeys.futureExpense(id), 'realizationSheet'],
    queryFn: () => futureExpenseService.getRealizationSheet(id),
    enabled: !!id,
  });
}

export function useCreateFutureExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ data, periodId }: { data: FutureExpenseCreate; periodId?: string }) =>
      futureExpenseService.create(data, periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Despesa futura criada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao criar meta', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useUpdateFutureExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FutureExpenseUpdate }) =>
      futureExpenseService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Meta atualizada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao atualizar meta', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useDeleteFutureExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => futureExpenseService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Meta removida com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao remover meta', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useRealizeFutureExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FutureExpenseRealizationCreate }) =>
      futureExpenseService.realize(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Investimento registrado com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao registrar investimento', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useReplanFutureExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { new_target_amount?: number; new_target_date?: string; reason?: string } }) =>
      futureExpenseService.replan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Meta replanejada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao replanejar meta', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useExecuteFutureExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FutureExpenseExecuteRequest }) =>
      futureExpenseService.execute(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Meta executada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao executar meta', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useFutureExpenseRealizations(expenseId: string) {
  return useQuery({
    queryKey: [...financialKeys.futureExpenses(), expenseId, 'realizations'],
    queryFn: () => futureExpenseService.getRealizations(expenseId),
    enabled: !!expenseId,
  });
}

export function useUpdateFutureExpenseRealization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ expenseId, realizationId, data }: { expenseId: string; realizationId: string; data: FutureExpenseRealizationUpdate }) =>
      futureExpenseService.updateRealization(expenseId, realizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Realização atualizada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao atualizar realização', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useDeleteFutureExpenseRealization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ expenseId, realizationId }: { expenseId: string; realizationId: string }) =>
      futureExpenseService.deleteRealization(expenseId, realizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Realização excluída com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao excluir realização', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

// ============================================
// Monthly Expense Hooks
// ============================================

export function useMonthlyExpenses(periodId?: string) {
  return useQuery({
    queryKey: financialKeys.monthlyExpenses(periodId),
    queryFn: () => monthlyExpenseService.getAll(periodId),
  });
}

export function useOverdueRevenues(periodId?: string) {
  return useQuery({
    queryKey: financialKeys.overdueRevenues(periodId),
    queryFn: () => revenueService.getOverdue(periodId),
    enabled: !!periodId,
  });
}

export function useUpcomingRevenues(daysAhead = UPCOMING_DAYS, periodId?: string) {
  return useQuery({
    queryKey: [...financialKeys.upcomingRevenues(periodId), daysAhead],
    queryFn: () => revenueService.getUpcoming(daysAhead, periodId),
    enabled: !!periodId,
  });
}

export function useOverdueExpenses(periodId?: string) {
  return useQuery({
    queryKey: financialKeys.overdueExpenses(periodId),
    queryFn: () => monthlyExpenseService.getOverdue(periodId),
    enabled: !!periodId,
  });
}

export function useUpcomingExpenses(daysAhead = UPCOMING_DAYS, periodId?: string) {
  return useQuery({
    queryKey: [...financialKeys.upcomingExpenses(periodId), daysAhead],
    queryFn: () => monthlyExpenseService.getUpcoming(daysAhead, periodId),
    enabled: !!periodId,
  });
}

export function useMonthlyExpense(id: string) {
  return useQuery({
    queryKey: financialKeys.monthlyExpense(id),
    queryFn: () => monthlyExpenseService.getById(id),
    enabled: !!id,
  });
}

export function useCreateMonthlyExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ data, periodId }: { data: MonthlyExpenseCreate; periodId?: string }) => 
      monthlyExpenseService.create(data, periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Despesa mensal criada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao criar despesa', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useUpdateMonthlyExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MonthlyExpenseUpdate }) =>
      monthlyExpenseService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Despesa atualizada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao atualizar despesa', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useDeleteMonthlyExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, apply_to_future }: { id: string; apply_to_future: boolean }) =>
      monthlyExpenseService.delete(id, { apply_to_future }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Despesa removida com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao remover despesa', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useRealizeMonthlyExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MonthlyExpenseRealization }) =>
      monthlyExpenseService.realize(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Despesa realizada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao realizar despesa', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useUndoMonthlyExpenseRealization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => monthlyExpenseService.undoRealization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Realizado excluído! A despesa voltou para planejado.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir realizado',
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
}

export function useCopyMonthlyExpensesFromPrevious() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => monthlyExpenseService.copyFromPrevious(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: `${data.copied_count} despesas copiadas com sucesso!` });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao copiar despesas', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

// ============================================
// Daily Expense Hooks
// ============================================

export function useDailyExpenses(periodId?: string) {
  return useQuery({
    queryKey: financialKeys.dailyExpenses(periodId),
    queryFn: () => dailyExpenseService.getAll(periodId),
  });
}

export function useDailyExpensesByPeriod(periodId?: string, enabled = true) {
  return useQuery({
    queryKey: financialKeys.dailyExpenses(periodId),
    queryFn: () => dailyExpenseService.getAll(periodId),
    enabled: enabled && !!periodId,
  });
}

export function useDailyExpense(id: string) {
  return useQuery({
    queryKey: financialKeys.dailyExpense(id),
    queryFn: () => dailyExpenseService.getById(id),
    enabled: !!id,
  });
}

export function useDailyExpenseSuggestion(id: string) {
  return useQuery({
    queryKey: [...financialKeys.dailyExpense(id), 'suggestion'],
    queryFn: () => dailyExpenseService.getSuggestion(id),
    enabled: !!id,
  });
}

export function useDailyExpensesSummaryByCategory() {
  return useQuery({
    queryKey: [...financialKeys.dailyExpenses(), 'summary-by-category'],
    queryFn: dailyExpenseService.getSummaryByCategory,
  });
}

export function useCreateDailyExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, periodId }: { data: DailyExpenseCreate; periodId?: string }) =>
      dailyExpenseService.create(data, periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Despesa diária criada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao criar despesa',
        description: getApiErrorMessage(error),
        variant: 'destructive'
      });
    },
  });
}

export function useUpdateDailyExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DailyExpenseUpdate }) =>
      dailyExpenseService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Despesa atualizada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao atualizar despesa', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useDeleteDailyExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => dailyExpenseService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Despesa removida com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao remover despesa',
        description: getApiErrorMessage(error),
        variant: 'destructive'
      });
    },
  });
}

export function useCopyDailyExpensesFromPrevious() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      target_period_id?: string;
      source_period_id?: string;
      copy_data?: 'planned' | 'realized';
      expense_ids?: string[];
    }) => dailyExpenseService.copyFromPrevious(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: `${data.copied_count} despesas copiadas com sucesso!` });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao copiar despesas',
        description: getApiErrorMessage(error),
        variant: 'destructive'
      });
    },
  });
}

export function useDailyExpenseCopySourcePeriods(
  targetPeriodId: string | undefined,
  copyData: 'planned' | 'realized' = 'planned',
  enabled = true
) {
  return useQuery({
    queryKey: financialKeys.dailyExpenseCopySourcePeriods(targetPeriodId, copyData),
    queryFn: () =>
      dailyExpenseService.getCopySourcePeriods({
        target_period_id: targetPeriodId!,
        copy_data: copyData,
      }),
    enabled: enabled && !!targetPeriodId,
  });
}

export function useRealizeDailyExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DailyExpenseRealizationCreate }) =>
      dailyExpenseService.realize(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Gasto registrado com sucesso!' });
    },
    onError: (error: Error) => {
      toast({ 
        title: 'Erro ao registrar gasto', 
        description: getApiErrorMessage(error),
        variant: 'destructive' 
      });
    },
  });
}

export function useUpdateDailyExpenseRealization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      expenseId,
      realizationId,
      data,
    }: {
      expenseId: string;
      realizationId: string;
      data: DailyExpenseRealizationUpdate;
    }) => dailyExpenseService.updateRealization(expenseId, realizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Lançamento atualizado com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar lançamento',
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteDailyExpenseRealization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, realizationId }: { expenseId: string; realizationId: string }) =>
      dailyExpenseService.deleteRealization(expenseId, realizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.all });
      toast({ title: 'Lançamento excluído com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir lançamento',
        description: getApiErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
}

// ============================================
// Dashboard Hooks
// ============================================

export function useDashboard(periodId?: string) {
  return useQuery({
    queryKey: financialKeys.dashboard(periodId),
    queryFn: () => dashboardService.getAll(periodId),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useDashboardKPIs(periodId?: string) {
  return useQuery({
    queryKey: financialKeys.kpis(periodId),
    queryFn: () => dashboardService.getKPIs(periodId),
    staleTime: 2 * 60 * 1000,
  });
}

export function useFinancialAlerts() {
  return useQuery({
    queryKey: financialKeys.alerts(),
    queryFn: dashboardService.getAlerts,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================
// Settings Hooks
// ============================================

export function useFinancialSettings() {
  return useQuery({
    queryKey: financialKeys.settings(),
    queryFn: financialSettingsService.get,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useUpdateFinancialSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: UserFinancialSettingsUpdate) => financialSettingsService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.settings() });
      toast({ title: 'Configurações atualizadas!' });
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar configurações', variant: 'destructive' });
    },
  });
}

export function useResetFinancialSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => financialSettingsService.reset(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.settings() });
      toast({ title: 'Configurações restauradas!' });
    },
    onError: () => {
      toast({ title: 'Erro ao restaurar configurações', variant: 'destructive' });
    },
  });
}
